import { useTranslation } from "react-i18next";
import {  Col, Row,Nav, Spinner } from "react-bootstrap";
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../../redux/store";
import  StatsForm  from "../components/tabs/StatsForm";
import  SurveyDefinitionForm  from "../components/tabs/SurveyDefinitionTab/SurveyDefinitionForm";
import { createSurvey, getSurveyFormById, updateSurvey } from "../api/surveyAPI";
import { useLocation, useParams } from "react-router-dom";
import { clearErrors, resetSurvey, setError, setSurveyForm } from "../../../../redux/surveySlice";
import SurveyParticipantsList from "../components/tabs/SurveyParticipantsTab/SurveyParticipantsList";
import SurveyQuestionsTab from "../components/tabs/SurveyQuestionsTab/SurveyQuestionsTab";
import { AnswerTypeEnum } from "../types/enum";
import { validateQuestionFormula } from "../utils/validateQuestionFormula";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { useNavigationGuard } from "../../../../contexts/NavigationGuardContext";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";

export default function SurveyForm() {
    const dispatch = useDispatch();
    const surveyForm = useSelector((state: RootState) => state.surveyForm.data);
    const { t } = useTranslation(['common', 'surveyAdmin']);
    const [tab, setTab] = useState("definition");
    const initialSurveySnapshot = useRef<string | null>(null);
    const navigate = useNavigate();
    const { setNavigationGuard, navigateWithGuard } = useNavigationGuard(); 
    const location = useLocation();
    
    const [loading, setLoading] = useState(false);

    const { surveyId } = useParams<{ surveyId: string }>();
    const [isPreview, setIsPreview] = useState(false);

    useEffect(() => {
        const requestedTab = (location.state as { tab?: string } | null)?.tab;
        if (requestedTab) {
            setTab(requestedTab);
        }
    }, [location.state]);

    const handleChange = (key: string) => {
        if (isPreview && key !== "questions") return;
        setTab(key);
    };

    useEffect(() => {
        if (!surveyId) {
            setNavigationGuard({
                enabled: true,
                message: t("surveyAdmin:form.modalExistWithUnsavedSurvey"),
                messageButtonSave: t("surveyAdmin:form.modalExistWithUnsavedSurveyMessageButtonSave"),
                messageButtonClose: t("surveyAdmin:form.modalExistWithUnsavedSurveyMessageButtonClose"),
                onSave: async (redirectTo, redirectState) => {
                    return await onSubmit(
                        redirectTo,
                        redirectState
                    );
                }
            });
            return;
        }
        let hasChanges = false;
        if (surveyForm.id == parseInt(surveyId)) {
            hasChanges = !!initialSurveySnapshot.current &&
                JSON.stringify(surveyForm) !== initialSurveySnapshot.current;
        }

        setNavigationGuard({
            enabled: hasChanges,
            message: t("surveyAdmin:form.modalExistWithUnsavedSurvey"),
            messageButtonSave: t("surveyAdmin:form.modalExistWithUnsavedSurveyMessageButtonSave"),
            messageButtonClose: t("surveyAdmin:form.modalExistWithUnsavedSurveyMessageButtonClose"),
            onSave: async () => { return await onSubmit(); }
        });

        return () => {
            setNavigationGuard({
                enabled: false,
                message: "",
                messageButtonSave:"",
                messageButtonClose:"",
            });
        };
    }, [surveyForm, setNavigationGuard, t, surveyId]);

    useEffect(() => {
        dispatch(resetSurvey());
        if (surveyId) {
            setLoading(true);
            getSurveyFormById(parseInt(surveyId))
                .then(data => {
                    dispatch(setSurveyForm(data));
                })
                .catch((err: ApiErrorResponse) => {
                    console.error(err);
                })
                .finally(() => setLoading(false));
        }

    }, [surveyId]);

    useEffect(() => {
        if (surveyId && surveyForm.id === parseInt(surveyId) && !initialSurveySnapshot.current) {
            initialSurveySnapshot.current = JSON.stringify(surveyForm);
        }
    }, [surveyForm, surveyId]);

    const saveSurvey = async()  => {
        dispatch(clearErrors())
        let isValid = true;

        if (!surveyForm.surveyName?.trim()) {
            dispatch(setError({ field: 'surveyName', message: t("common:form.requiredFieldForm") }));
            isValid = false;
        }
        if (!surveyForm.campaignId) {
            dispatch(setError({ field: 'campaignId', message: t("common:form.requiredFieldForm") }));
            isValid = false;
        }
        if (!surveyForm.startDate) {
            dispatch(setError({ field: 'startDate', message: t("common:form.requiredFieldForm") }));
            isValid = false;
        }
        if (!surveyForm.endDate) {
            dispatch(setError({ field: 'endDate', message: t("common:form.requiredFieldForm") }));
            isValid = false;
        }
        const allowedCodes = surveyForm.questions
            .map((q) => q.code)
            .filter((code): code is string => !!code);

        surveyForm.questions.forEach((question, index) => {
            if (!question.text?.catalan) {
                dispatch(setError({ field: `questions[${index}].text.catalan`, message: t("common:form.requiredFieldForm") }));
                isValid = false;
            }
            if (!question.helperText?.catalan) {
                dispatch(setError({ field: `questions[${index}].helperText.catalan`, message: t("common:form.requiredFieldForm") }));
                isValid = false;
            }
            if (question.answerType == AnswerTypeEnum.Option) {
                question.answerOptions.forEach((answer, answerIndex) => {
                    if (!answer.text?.catalan) {
                        dispatch(setError({ field: `questions[${index}].answerOptions[${answerIndex}].text.catalan`, message: t("common:form.requiredFieldForm") }));
                        isValid = false;
                    }
                })
            }
            if (question.answerType == AnswerTypeEnum.Formula) {
                const formulaValue = question.formula?.trim() ?? "";
                if (!formulaValue) {
                    dispatch(setError({ field: `questions[${index}].formula`, message: t("common:form.requiredFieldForm") }));
                    isValid = false;
                } else {
                    const formulaErrorKey = validateQuestionFormula(formulaValue, allowedCodes);
                    if (formulaErrorKey) {
                        dispatch(setError({ field: `questions[${index}].formula`, message: t(`surveyAdmin:form.formulaError.${formulaErrorKey}`) }));
                        isValid = false;
                    }
                }
            }
        })

        try {
            if (isValid) {
                if (surveyForm.id != 0)
                    await updateSurvey(surveyForm.id!, surveyForm);
                else
                    await createSurvey(surveyForm);
                dispatch(resetSurvey());
                return true;
            } else { 
                return false;
            }
        } catch (error) {
            console.error("Error creating survey", error);
        }
    }

    const onSubmit = async (redirectTo?: string, redirectState?: unknown) => {
        const save = await saveSurvey();

        if (!save) return false;

        const alerts = [];

        if (
            !surveyForm.sendByEmail
            && !surveyForm.sendBySMS
        ) {

            alerts.push({
                message: t(
                    "surveyAdmin:form.savedWithoutSendingMethodMessage"
                ),
                type: "warning"
            });
        }

        alerts.push({
            message: t(
                "surveyAdmin:saveSurveySuccessfully"
            ),
            type: "success"
        });
        
        navigate(
            redirectTo ?? "/admin/surveyList",
            {
                state: redirectState ?? { alerts }
            }
        );
        return true;
      
    }

    const onCancel = () => {
        navigateWithGuard('/admin/surveyList');
      
       
    }

    return (
        <div>
            {loading && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            <Row className="mb-2">
                <Col xs={8} md={8}>
                    <h2>{!surveyId ? t("surveyAdmin:newSurvey") : `${t("surveyAdmin:survey")} #${surveyId}`}</h2>
                </Col>
                <Col xs={4} md={4} className="text-end">
                    <GenericSaveButton
                        onClick={() => onSubmit()}
                        onCancelClick={onCancel}
                        cancelLabel={surveyForm.id != 0 ? t("common:buttons.buttonBack") : t("common:buttons.buttonCancel")}
                        saveLabel={t("common:save")}
                    />
                </Col>
            </Row>

            <Nav variant="tabs" activeKey={tab}>
                <Nav.Item>
                    <Nav.Link
                        eventKey="definition"
                        onClick={() => handleChange("definition")}
                        className={`survey-tab-link ${tab === "definition" ? "active-tab" : ""}`}
                        disabled={isPreview}
                    >
                        {t("surveyAdmin:surveyDefinition")}
                    </Nav.Link>
                </Nav.Item>

                <Nav.Item>
                    <Nav.Link
                        eventKey="questions"
                        onClick={() => handleChange("questions")}
                        className={`survey-tab-link ${tab === "questions" ? "active-tab" : ""}`}
                        disabled={isPreview}
                    >
                        {t("surveyAdmin:questionsAndAnswers")}
                    </Nav.Link>
                </Nav.Item>
                { surveyId &&
                    <Nav.Item>
                        <Nav.Link
                            eventKey="participants"
                            onClick={() => handleChange("participants")}
                            className={`survey-tab-link ${tab === "participants" ? "active-tab" : ""}`}
                            disabled={isPreview}
                        >
                            {t("surveyAdmin:participants")}
                        </Nav.Link>
                    </Nav.Item>
                }
                { surveyId &&
                    <Nav.Item>
                        <Nav.Link
                            eventKey="stats"
                            onClick={() => handleChange("stats")}
                            className={`survey-tab-link ${tab === "stats" ? "active-tab" : ""}`}
                            disabled={isPreview}
                        >
                            {t("surveyAdmin:statistics")}
                        </Nav.Link>
                    </Nav.Item>
                }
            </Nav>

            <div className="mt-3">
                {tab === "definition" && <SurveyDefinitionForm />}
                {tab === "questions" && <SurveyQuestionsTab onPreviewChange={setIsPreview} />}
                {tab === "participants" && <SurveyParticipantsList />}
                {tab === "stats" && <StatsForm />}
            </div>
        </div>
    ); 

}


